import React, { useMemo } from 'react';
import ColorCodedCell from './ColorCodedCell';
import './OverviewTable.css';

export default function OverviewTable({ 
  data, 
  columns, 
  globalRanges, 
  rowKey, 
  rowLabel, 
  emptyMessage = "No data available",
  maxHeight = '500px',
  useTableSpecificRanges = false,
  onRowClick = null, 
  isClickable = false, 
  tableType = 'generic' 
}) {
  // Calculate table-specific ranges for this data only 
  const tableSpecificRanges = useMemo(() => {
    if (!data || data.length === 0) return {};
    
    const ranges = {};
    
    columns.forEach(col => {
      const values = data.map(row => {
        let value = row[col.key] || 0;
        return value;
      }).filter(val => val > 0);
      
      ranges[col.type] = {
        min: 0,
        max: values.length > 0 ? Math.max(...values) : 1
      };
    });
    
    return ranges;
  }, [data, columns]);

  // Use either table-specific or global ranges
  const colorRanges = useTableSpecificRanges ? tableSpecificRanges : globalRanges;

  const maxValues = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        alerts: 0,
        waterWaste: 0,
        chemicalWaste: 0,
        timeWaste: 0
      };
    }

    const getMaxValue = (columnKey) => {
      const values = data.map(row => {
        let value = row[columnKey] || 0;
        if (columnKey === 'timeWaste') {
          value = value / 60;
        }
        return value;
      }).filter(val => val > 0);
      
      return values.length > 0 ? Math.max(...values) : 0;
    };

    return {
      alerts: getMaxValue('alerts'),
      waterWaste: getMaxValue('waterWaste'),
      chemicalWaste: getMaxValue('chemicalWaste'),
      timeWaste: getMaxValue('timeWaste')
    };
  }, [data]);

  const getSafeRange = (columnType) => {
    const range = colorRanges?.[columnType];
    if (!range || range.max === undefined || range.min === undefined) {
      return { min: 0, max: 1 };
    }
    return range;
  };

  // Handle row click navigation
  const handleRowClick = (row) => {
    if (!isClickable || !onRowClick) return;
    
    onRowClick(row, tableType);
  };

  if (!data || data.length === 0) {
    return (
      <div className="overview-table-empty-state">
        <div className="overview-table-empty-icon">
          <span role="img" aria-label="Chart icon">📊</span>
        </div>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overview-table-container">
      <div 
        className="overview-table-scroll-container"
        style={{ maxHeight }} // Keep dynamic maxHeight inline
      >
        <table className="overview-table">
          <thead className="overview-table-header">
            <tr>
              <th className="overview-table-header-cell">
                {rowLabel}
              </th>
              {columns.map((col, index) => (
                <th 
                  key={col.key} 
                  className={`overview-table-header-cell center ${
                    index === columns.length - 1 ? 'last-column' : ''
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr 
                key={row[rowKey] || rowIndex} 
                className={`overview-table-body-row ${
                  rowIndex === data.length - 1 ? 'last-row' : ''
                } ${isClickable ? 'clickable-row' : ''}`}
                onClick={() => handleRowClick(row)}
                style={{
                  cursor: isClickable ? 'pointer' : 'default'
                }}
              >
                <td className="overview-table-row-label-cell">
                  {row[rowKey]}
                </td>
                {columns.map((col, colIndex) => {
                  let colorValue = row[col.key] || 0;
                  if (col.key === 'timeWaste') {
                    colorValue = colorValue / 60;
                  }

                  const displayValue = col.formatter ? col.formatter(row[col.key] || 0) : (row[col.key] || 0);
                  
                  const isMaxValue = colorValue === maxValues[col.type] && colorValue > 0;
                  const isLastColumn = colIndex === columns.length - 1;
                  
                  return (
                    <ColorCodedCell
                      key={col.key}
                      value={colorValue}
                      displayValue={displayValue}
                      range={getSafeRange(col.type)}
                      isMaxValue={isMaxValue}
                      isLastColumn={isLastColumn}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
